import React, { useEffect, useState } from 'react';
import ScrollContainer from '../../../../Base/Components/ScrollContainer';
import { useNCZIContext } from '../../../Services/NCZIContext';
import { useEffectAsync } from '../../../../../helpers';
import NCZICommandResult from '../../NCZICommandResult/NCZICommandResult';
import ElectronicDPNFilter from '../Filter/ElectronicDPNFilter';
import useNCZIElectronicDPN from '../../../Hooks/useNCZIElectronicDPN';
import EHRResult from '../../EHR/EHRResult/EHRResult';
import PatientConsentButton from '../../PatientConsentModal/PatientConsentButton';
import { PatientConsentType } from '../../../Commands/WritePatientConsent';
import NCZIListPagination from '../../NCZIListPagination/NCZIListPagination';
import { Theme } from '../../../../../Enums/Theme.enum';
import LinkButton from '../../../../../Components/Button/LinkButton';
import ElectronicDPNCreateModal from '../Create/ElectronicDPNCreateModal';
import ElectronicDPNListItems from './ElectronicDPNListItems';

//@ts-ignore
import styles from './ElectronicDPNList.module.scss';

interface Props {
	setIsLoading: (isLoading: boolean) => void;
}

const AT_PN_DATE_TO =
	'CEN-EN13606-ENTRY.PotvrdenieDocasnejPraceneschopnosti.v1/at0003';

const ElectronicDPNList: React.FC<Props> = () => {
	const [isCreateDisabled, setIsCreateDisabled] = useState<boolean>(true);

	const {
		patientDPNData,
		patientDPNPage,
		patientIdentifier,
		patientDPNFilter,
	} = useNCZIContext();

	const {
		handleLoadPatientDPNData,
		handleFakeLoadPatientDPNData,
		loadingCommandId,
	} = useNCZIElectronicDPN();

	const [electronicDPNCreateOpen, setElectronicDPNCreateOpen] =
		useState<boolean>(false);

	const handleLoadData = async () => {
		if (!patientDPNData) {
			await handleLoadPatientDPNData(1, patientDPNFilter);
			// await handleFakeLoadPatientDPNData();
		}
	};

	useEffectAsync(async () => {
		await handleLoadData();
	}, []);

	const handleCloseDetailModal = async (needReload: boolean) => {
		if (needReload) {
			await handleLoadPatientDPNData(patientDPNPage, patientDPNFilter);
		}
	};

	const handleCloseElectronicDPNCreate = async (needReload: boolean) => {
		setElectronicDPNCreateOpen(false);
		if (needReload) {
			await handleLoadPatientDPNData(1, patientDPNFilter);
		}
	};

	useEffect(() => {
		const tmpDpnItems = patientDPNData?.ehrExtract?.items[0]?.items || [];
		if (tmpDpnItems.length > 0) {
			let allItemsHaveEnd = true;
			for (const dpnItem of tmpDpnItems) {
				let itemHaveEnd = false;
				if (dpnItem?.entry?.info?.version === 'VER04') {
					itemHaveEnd = true;
				} else {
					for (const entryItem of dpnItem?.entry?.items || []) {
						if (entryItem.element) {
							if (AT_PN_DATE_TO === entryItem.element.archetypeId) {
								itemHaveEnd = true;
							}
						}
					}
				}
				if (!itemHaveEnd) {
					allItemsHaveEnd = false;
				}
			}
			setIsCreateDisabled(!allItemsHaveEnd);
		} else {
			if (!patientDPNData) {
				setIsCreateDisabled(true);
			} else {
				setIsCreateDisabled(false);
			}
		}
	}, [patientDPNData]);

	const dpnItems = patientDPNData?.ehrExtract?.items[0]?.items || [];

	return (
		<div className={styles.wrapper}>
			<div className={styles.filterWrapper}>
				<ElectronicDPNFilter
					onFilter={values => handleLoadPatientDPNData(1, values)}
				/>
			</div>
			<div className={styles.contentWrapper}>
				<div className={styles.controls}>
					<LinkButton
						title='nczi.electronicDPN.create.title'
						theme={Theme.BLUE}
						onClick={() => setElectronicDPNCreateOpen(true)}
						firstUpper
						disabled={isCreateDisabled}
					/>
					<PatientConsentButton
						loadData={() => handleLoadPatientDPNData(1, patientDPNFilter)}
						code={patientDPNData?.result?.code}
						consentType={PatientConsentType.PSU}
					/>
				</div>
				<ScrollContainer>
					<div className={styles.content}>
						{!patientDPNData || !patientIdentifier ? (
							<div className={styles.loadingWrapper}>
								<NCZICommandResult
									commandId={loadingCommandId}
									handleRetryCommand={() =>
										handleLoadPatientDPNData(patientDPNPage, patientDPNFilter)
									}
								/>
							</div>
						) : (
							<>
								<EHRResult ehrResult={patientDPNData?.result} />
								<ElectronicDPNListItems
									items={dpnItems}
									onCloseDetail={handleCloseDetailModal}
									patientIdentifier={patientIdentifier}
									disableCreate={isCreateDisabled}
								/>
								{/*<EHRExtractUnpackCompositionItems*/}
								{/*	ehrExtract={patientDPNData?.ehrExtract}*/}
								{/*	patientIdentifier={patientIdentifier}*/}
								{/*	onCloseDetail={handleCloseDetailModal}*/}
								{/*/>*/}
								<NCZIListPagination
									hasMoreData={dpnItems.length > 0}
									page={patientDPNPage ?? 1}
									loadData={page =>
										handleLoadPatientDPNData(page, patientDPNFilter)
									}
								/>
							</>
						)}
					</div>
				</ScrollContainer>
			</div>
			<ElectronicDPNCreateModal
				opened={electronicDPNCreateOpen}
				onClose={handleCloseElectronicDPNCreate}
				patientIdentifier={patientIdentifier!}
			/>
		</div>
	);
};

export default ElectronicDPNList;
